// ─── src/components/gewFeedback/FeedbackCharts.tsx ───────────────────────────
//
// Charts and lists for GEW attendee feedback, shared by the staff
// "GEW Feedback" view (all events or one event) and the catalyst portal
// (one meetup). One chart per rating question plus the NPS distribution.

import type { FC } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Cell, Tooltip, ResponsiveContainer, LabelList,
} from "recharts";
import { MessageSquare, Percent, Star, TrendingUp, Quote } from "lucide-react";
import { StatCard } from "../ui/StatCard";
import { HorizontalBarChart } from "../dashboard/HorizontalBarChart";
import type {
  FeedbackAnswers, FeedbackExportRow, FeedbackSummary, RatingStats,
} from "../../api/gewFeedback";

// Answer keys match mainwebsite-backend's submit-gew-feedback.dto.ts; the
// Arabic labels are the ones attendees saw on the form.
export const NEXT_STEP_LABEL: Record<string, string> = {
  start_idea: "البدء بالعمل على فكرة مشروع",
  grow_project: "تطوير مشروع قائم",
  connect_people: "التواصل مع أشخاص تعرّفوا إليهم",
  attend_more: "حضور فعاليات أو برامج ريادية أخرى",
  seek_funding: "البحث عن تمويل أو احتضان",
  nothing_now: "لا شيء حالياً",
  other: "أخرى",
};
export const HEAR_ABOUT_LABEL: Record<string, string> = {
  organizer: "من الجهة المنظِّمة مباشرة",
  gew_website: "موقع أسبوع الريادة العالمي",
  facebook: "فيسبوك",
  instagram: "إنستغرام",
  linkedin: "لينكدإن",
  whatsapp_group: "مجموعة واتساب",
  friend: "صديق أو زميل",
  other: "أخرى",
};

const RATING_QUESTIONS: [keyof FeedbackSummary["ratings"], string, string][] = [
  ["ratingOverall", "Overall experience", "بشكل عام، كيف تقيّم تجربتك في هذه الفعالية؟"],
  ["ratingContent", "Content", "إلى أي حدّ كان محتوى الفعالية مفيداً ومناسباً لاحتياجاتك؟"],
  ["ratingSpeakers", "Speakers / trainers", "كيف تقيّم المتحدثين أو المدرّبين؟"],
  ["ratingOrganization", "Organisation", "كيف تقيّم تنظيم الفعالية: المكان أو المنصة، والتوقيت، والالتزام بالبرنامج؟"],
];

const AXIS = { fill: "#9ca3af", fontSize: 11 };
const NPS_COLOR = (v: number) => (v <= 6 ? "#ef4444" : v <= 8 ? "#f59e0b" : "#00DC92");
const RATING_COLORS = ["#ef4444", "#f97316", "#f59e0b", "#34d399", "#00DC92"];

const ChartTooltip = ({
  active, payload, unit,
}: {
  active?: boolean;
  payload?: { payload: { label: string; value: number; pct: number } }[];
  unit: string;
}) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-brand-card border border-brand-border rounded-xl px-4 py-3 shadow-xl text-sm">
      <p className="text-white font-semibold">{unit} {p.label}</p>
      <p className="text-brand-muted mt-0.5">
        {p.value} response{p.value !== 1 ? "s" : ""}
        <span className="text-brand-green ml-2">({p.pct}%)</span>
      </p>
    </div>
  );
};

const pct = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);

export const FeedbackKpis: FC<{ summary: FeedbackSummary }> = ({ summary: s }) => (
  <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
    <StatCard icon={MessageSquare} label="Responses" value={s.responses} accent="bg-brand-green" />
    <StatCard
      icon={Percent}
      label={`Response rate (of ${s.emailed} emailed)`}
      value={s.responseRate == null ? "—" : `${s.responseRate}%`}
      accent="bg-brand-teal"
    />
    <StatCard
      icon={Star}
      label="Overall rating"
      value={s.ratings.ratingOverall.average == null ? "—" : `${s.ratings.ratingOverall.average} / 5`}
      accent="bg-brand-green"
    />
    <StatCard icon={TrendingUp} label="NPS (−100 to +100)" value={s.nps.score ?? "—"} accent="bg-brand-teal" />
    <StatCard icon={Quote} label="Quotes to publish" value={s.quotes} accent="bg-brand-green" />
  </div>
);

export const RatingChart: FC<{ title: string; question: string; stats: RatingStats; note?: string }> = ({
  title, question, stats, note,
}) => {
  const data = [1, 2, 3, 4, 5].map((v) => ({
    label: String(v),
    value: stats.distribution[v - 1],
    pct: pct(stats.distribution[v - 1], stats.answered),
  }));
  return (
    <div className="card p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-white font-semibold text-sm">{title}</p>
          <p className="text-brand-muted text-xs mt-0.5 truncate" dir="rtl" title={question}>{question}</p>
        </div>
        <p className="text-brand-green text-xl font-bold shrink-0">
          {stats.average == null ? "—" : stats.average}
          <span className="text-brand-muted text-xs font-normal"> / 5</span>
        </p>
      </div>
      <div className="h-44 mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 18, right: 4, bottom: 0, left: -24 }}>
            <XAxis dataKey="label" tick={AXIS} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={AXIS} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTooltip unit="Rating" />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={42}>
              {data.map((_, i) => <Cell key={i} fill={RATING_COLORS[i]} />)}
              <LabelList dataKey="value" position="top" fill="#9ca3af" fontSize={11} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-brand-muted text-xs mt-2">
        {stats.answered} answer{stats.answered !== 1 ? "s" : ""}{note ? ` · ${note}` : ""}
      </p>
    </div>
  );
};

export const NpsChart: FC<{ nps: FeedbackSummary["nps"] }> = ({ nps }) => {
  const total = nps.promoters + nps.passives + nps.detractors;
  const data = nps.distribution.map((value, v) => ({ label: String(v), value, pct: pct(value, total) }));
  return (
    <div className="card p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-white font-semibold text-sm">Net Promoter Score</p>
          <p className="text-brand-muted text-xs mt-0.5" dir="rtl">
            ما مدى احتمال أن توصي أصدقاءك أو زملاءك بحضور فعاليات أسبوع الريادة العالمي؟ (0–10)
          </p>
        </div>
        <p className="text-brand-green text-xl font-bold shrink-0">{nps.score ?? "—"}</p>
      </div>
      <div className="h-52 mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 18, right: 4, bottom: 0, left: -24 }}>
            <XAxis dataKey="label" tick={AXIS} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={AXIS} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTooltip unit="Score" />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={36}>
              {data.map((_, v) => <Cell key={v} fill={NPS_COLOR(v)} />)}
              <LabelList dataKey="value" position="top" fill="#9ca3af" fontSize={11} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs mt-3 text-brand-muted">
        <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5" style={{ background: "#00DC92" }} />Promoters (9–10): {nps.promoters} · {pct(nps.promoters, total)}%</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5" style={{ background: "#f59e0b" }} />Passives (7–8): {nps.passives} · {pct(nps.passives, total)}%</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5" style={{ background: "#ef4444" }} />Detractors (0–6): {nps.detractors} · {pct(nps.detractors, total)}%</span>
        <span>NPS = % promoters − % detractors{nps.average != null ? ` · average ${nps.average}` : ""}</span>
      </div>
    </div>
  );
};

const CategoryChart: FC<{ title: string; counts: Record<string, number>; labels: Record<string, string>; total: number }> = ({
  title, counts, labels, total,
}) => {
  const data = Object.entries(counts).map(([k, value]) => ({
    name: labels[k] ?? k,
    value,
    percentage: String(pct(value, total)),
  }));
  return (
    <div className="card p-5">
      <p className="text-white font-semibold text-sm">{title}</p>
      <div className="h-64 mt-3" dir="ltr">
        <HorizontalBarChart data={data} />
      </div>
    </div>
  );
};

/** All charts for one summary: a chart per rating question, NPS, and the two choice questions. */
export const FeedbackCharts: FC<{ summary: FeedbackSummary }> = ({ summary: s }) => {
  if (s.responses === 0) {
    return <div className="card p-10 text-center text-brand-muted">No responses yet.</div>;
  }
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {RATING_QUESTIONS.map(([k, title, q]) => (
          <RatingChart
            key={k}
            title={title}
            question={q}
            stats={s.ratings[k]}
            note={k === "ratingSpeakers" && s.speakersNotApplicable ? `${s.speakersNotApplicable} chose "not applicable"` : undefined}
          />
        ))}
      </div>
      <NpsChart nps={s.nps} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CategoryChart title="Planned next step (multi-choice)" counts={s.nextSteps} labels={NEXT_STEP_LABEL} total={s.responses} />
        <CategoryChart title="How they heard about the event" counts={s.hearAbout} labels={HEAR_ABOUT_LABEL} total={s.responses} />
      </div>
    </div>
  );
};

/** Every response with the respondent's name (from their registration). */
export const ResponsesList: FC<{ rows: (FeedbackAnswers & { event?: FeedbackExportRow["event"] })[] }> = ({ rows }) => {
  if (!rows.length) return null;
  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-4 border-b border-brand-border">
        <p className="text-white font-semibold text-sm">Responses ({rows.length})</p>
      </div>
      <ul className="divide-y divide-brand-border">
        {rows.map((r) => (
          <li key={r.id} className="px-5 py-4" dir="rtl">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-white font-medium">{r.attendee?.fullName ?? "—"}</p>
                <p className="text-brand-muted text-xs" dir="ltr" style={{ textAlign: "right" }}>
                  {r.attendee?.email}{r.attendee?.whatsapp ? ` · ${r.attendee.whatsapp}` : ""}
                </p>
                {r.event && <p className="text-brand-green text-xs mt-0.5">{r.event.title}</p>}
              </div>
              <p className="text-brand-muted text-xs" dir="ltr">{new Date(r.submittedAt).toLocaleString()}</p>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3 text-xs" dir="ltr" style={{ justifyContent: "flex-end" }}>
              <Pill label="Overall" value={`${r.ratingOverall}/5`} />
              <Pill label="Content" value={`${r.ratingContent}/5`} />
              <Pill label="Speakers" value={r.speakersNotApplicable ? "N/A" : `${r.ratingSpeakers}/5`} />
              <Pill label="Organisation" value={`${r.ratingOrganization}/5`} />
              <Pill label="NPS" value={`${r.nps}/10`} />
            </div>
            <p className="text-brand-muted text-xs mt-3">
              الخطوة التالية: <span className="text-white">{nextStepsText(r)}</span>
              {" · "}عرف بالفعالية: <span className="text-white">{hearAboutText(r)}</span>
            </p>
            {([
              ["أكثر ما استفاد منه", r.mostUseful],
              ["مواضيع أو متحدثون مقترحون", r.wantedTopics],
              ["ما كان سيحسّنه", r.improvement],
              ["ملاحظات أخرى", r.notes],
            ] as [string, string | null][])
              .filter(([, t]) => t)
              .map(([label, t]) => (
                <div key={label} className="mt-2 text-sm">
                  <p className="text-brand-muted text-xs">{label}</p>
                  <p className="text-white whitespace-pre-wrap">{t}</p>
                </div>
              ))}
            {r.quoteConsent && r.quote && (
              <p className="mt-3 rounded-lg bg-brand-green/10 border border-brand-green/30 px-3 py-2 text-sm text-white">
                «{r.quote}»{r.quoteName ? <span className="text-brand-muted"> — {r.quoteName}</span> : null}
                <span className="block text-brand-green text-[11px] mt-0.5">وافق على نشر رأيه</span>
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Pill: FC<{ label: string; value: string }> = ({ label, value }) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-brand-border px-2.5 py-0.5">
    <span className="text-brand-muted">{label}</span>
    <span className="text-white font-semibold">{value}</span>
  </span>
);

const nextStepsText = (r: FeedbackAnswers) =>
  r.nextSteps
    .map((k) => (k === "other" && r.nextStepOther ? `أخرى: ${r.nextStepOther}` : NEXT_STEP_LABEL[k] ?? k))
    .join("، ");
const hearAboutText = (r: FeedbackAnswers) =>
  r.hearAbout === "other" && r.hearAboutOther ? `أخرى: ${r.hearAboutOther}` : HEAR_ABOUT_LABEL[r.hearAbout] ?? r.hearAbout;

/** CSV (UTF-8 with BOM so Excel shows Arabic) of every answer, one row per response. */
export function exportFeedbackCsv(rows: FeedbackExportRow[], filename: string) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = [
    "Event", "Organiser", "Date", "Governorate", "City", "Event type",
    "Attendee", "Email", "WhatsApp",
    "Overall (1–5)", "NPS (0–10)", "Most useful", "Content (1–5)", "Speakers (1–5)", "Organisation (1–5)",
    "Next steps", "Wanted topics/speakers", "Improvement",
    "Quote consent", "Quote", "Name for quote",
    "Heard about via", "Notes", "Submitted at",
  ].join(",");
  const body = rows.map((r) =>
    [
      r.event?.title, r.event?.organizerName, r.event?.eventDate, r.event?.governorate, r.event?.city,
      r.event?.sourceType === "meetup" ? "Catalyst meetup" : r.event?.type,
      r.attendee?.fullName, r.attendee?.email, r.attendee?.whatsapp,
      r.ratingOverall, r.nps, r.mostUseful, r.ratingContent,
      r.speakersNotApplicable ? "لا ينطبق" : r.ratingSpeakers, r.ratingOrganization,
      nextStepsText(r), r.wantedTopics, r.improvement,
      r.quoteConsent ? "Yes" : "No", r.quote, r.quoteName,
      hearAboutText(r), r.notes, r.submittedAt,
    ].map(esc).join(","),
  );
  const blob = new Blob(["﻿" + [header, ...body].join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
